'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormDefinition } from '@akademi/form-designer/schema';
import { DynamicFormRenderer, type ValidationLabels } from '../form/DynamicFormRenderer';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SelectField,
  AsyncSelect,
  type AsyncSelectOption,
} from '@akademi/ui';
import { ignoreSbSelectPortalOutsideClick } from '../_shared/dialogOutsideClick';
import { applyEducationLevelGate, type EducationLevelGate } from './educationLevelGate';

// ---------------------------------------------------------------------------
// ActivityApplicationModal — reusable "Başvur" (apply) flow for external
// events, ported from akademi-lms-v2's ActivityApplicationForm.
//
// Opens a dialog that:
//   1. GETs the signed-in user's saved person + education details to
//      prefill the form (/api/activities/application-form),
//   2. lets them complete account + (cascading) education information,
//   3. asks them to confirm (or correct) the email every event notification
//      will be sent to,
//   4. POSTs the application (/api/activities/{eventId}/register).
//
// Self-contained (inline CSS + portal-route fetches) so any block can drop
// it in. ActivityRow's card-2 "Başvur" button is the first consumer.
// ---------------------------------------------------------------------------

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
const DEFAULT_COUNTRY_CODE = '90';

/** Backend education-level code -> catalogue key. Resolved inside the
 *  component so a language switch re-labels the options. */
const EDU_LEVEL_LABEL_KEYS: Record<string, string> = {
  PRIMARY_SCHOOL: 'activity.educationLevel.primarySchool',
  MIDDLE_SCHOOL: 'activity.educationLevel.middleSchool',
  HIGH_SCHOOL: 'activity.educationLevel.highSchool',
  ASSOCIATE_DEGREE: 'activity.educationLevel.associateDegree',
  BACHELORS_DEGREE: 'activity.educationLevel.bachelorsDegree',
  MS_MBA: 'activity.educationLevel.msMba',
  DOCTORAL: 'activity.educationLevel.doctoral',
};

/** Education levels for which the university cascade is hidden. */
const NON_TERTIARY = ['PRIMARY_SCHOOL', 'MIDDLE_SCHOOL', 'HIGH_SCHOOL'];

/** Overridable copy — defaults come from the block catalogue. */
export interface ActivityApplicationLabels {
  title: string;
  note: string;
  accountInformation: string;
  educationInformation: string;
  name: string;
  surname: string;
  identificationNumber: string;
  email: string;
  mobile: string;
  dob: string;
  currentCity: string;
  educationStatus: string;
  educationLevel: string;
  universityCity: string;
  universityName: string;
  facultyName: string;
  departmentName: string;
  className: string;
  notGraduate: string;
  graduate: string;
  preparatoryClass: string;
  cancel: string;
  apply: string;
  success: string;
  /** Email-check step (9470). */
  emailCheckTitle: string;
  emailCheckNote: string;
  emailCheckBack: string;
  emailCheckConfirm: string;
  errorGeneric: string;
  /** Apply-gate rejections (9477), keyed by the backend's EligibilityIssueType. */
  errorBlacklisted: string;
  errorAgeBelowMin: string;
  errorAgeAboveMax: string;
  errorDateOfBirthRequired: string;
  errorEducationLevelRequired: string;
  errorEducationLevelNotAllowed: string;
  errorEducationLevelNotVerified: string;
  errorAlreadyAttendedInGroup: string;
  errorPrerequisitesNotMet: string;
  errorAlreadyRegistered: string;
  /** Availability rejections (9477). */
  errorEventNotAvailable: string;
  errorEntriesClosed: string;
  errorQuotaFull: string;
  /** Pre-flight block panel (9476/9477). */
  blockedTitle: string;
  blockedPrerequisites: string;
  requiredField: string;
  emailField: string;
  idNumberField: string;
  telField: string;
  noResult: string;
  loading: string;
  select: string;
}

/**
 * Backend `EligibilityIssueType` → label key. The register endpoint rejects
 * with one of these codes; the pre-flight `/eligibility` endpoint returns the
 * same set, so both surfaces read identically.
 */
const BLOCK_REASON_LABELS: Record<string, keyof ActivityApplicationLabels> = {
  BLACKLISTED: 'errorBlacklisted',
  AGE_BELOW_MIN: 'errorAgeBelowMin',
  AGE_ABOVE_MAX: 'errorAgeAboveMax',
  DATE_OF_BIRTH_REQUIRED: 'errorDateOfBirthRequired',
  EDUCATION_LEVEL_REQUIRED: 'errorEducationLevelRequired',
  EDUCATION_LEVEL_NOT_ALLOWED: 'errorEducationLevelNotAllowed',
  EDUCATION_LEVEL_NOT_VERIFIED: 'errorEducationLevelNotVerified',
  ALREADY_ATTENDED_IN_GROUP: 'errorAlreadyAttendedInGroup',
  PREREQUISITES_NOT_MET: 'errorPrerequisitesNotMet',
  EVENT_NOT_AVAILABLE: 'errorEventNotAvailable',
  ENTRIES_CLOSED: 'errorEntriesClosed',
  QUOTA_FULL: 'errorQuotaFull',
  ALREADY_REGISTERED: 'errorAlreadyRegistered',
};

/**
 * Turn a rejected register response into the reason to show (9477).
 *
 * <p>Matched against the raw body rather than a fixed field: the error envelope
 * is produced by gobito's `ErrorHandler`, which ships as a compiled dependency,
 * so the exact key isn't verifiable from source here. The codes are distinctive
 * enough that a scan is unambiguous, and anything unrecognised falls through to
 * the generic message.</p>
 */
async function blockReason(res: Response, labels: ActivityApplicationLabels): Promise<string> {
  if (res.status === 409) return labels.errorAlreadyRegistered;

  let body = '';
  try {
    body = await res.text();
  } catch {
    return labels.errorGeneric;
  }

  // The gate reports EVERY failing code, comma-joined, so collect them all —
  // showing only the first would send the applicant back for a second refusal.
  // ALREADY_ATTENDED_IN_GROUP contains no other code as a substring, but
  // ALREADY_REGISTERED does not either; a plain scan stays unambiguous.
  const hits = Object.entries(BLOCK_REASON_LABELS)
    .filter(([code]) => body.includes(code))
    .map(([, key]) => labels[key]);
  return hits.length > 0 ? hits.join(' ') : labels.errorGeneric;
}

/** `ExternalEventEligibilityDto` — the pre-flight response. */
interface EligibilityResponse {
  eligible?: boolean;
  issues?: { code?: string; reason?: string; relatedEventTitle?: string | null }[];
  unmetPrerequisites?: {
    targetTitle?: string;
    reason?: string;
    /** The course/event's own id — what the link needs (9477). */
    targetId?: number | null;
    /** 'COURSE' | 'EVENT'. */
    targetType?: string;
  }[];
}

/**
 * What the pre-flight decided, once. `null` = not screened (still loading, or
 * the check itself failed — a pre-flight that cannot answer must not stand in
 * for a rejection, so the form opens and the register POST stays the gate).
 */
interface BlockState {
  reasons: string[];
  unmet: { label: string; href?: string }[];
}

/**
 * Where an unmet prerequisite sends the applicant (9477 "düzeltme
 * yönlendirmesi"). Without a target id there is nothing to link to, so the row
 * renders as plain text rather than a link that goes nowhere.
 *
 * <p>COURSE only. `/lxp/course/{detail}` reads the id off the last dash
 * segment, so a bare id resolves. An EVENT prerequisite gets NO link: the
 * portal has no event detail route — `/activities/{type}` is the
 * upcoming/missed/previous bucket, so `/activities/{id}` would quietly render
 * the wrong page rather than fail.</p>
 */
function prerequisiteHref(targetType?: string, targetId?: number | null): string | undefined {
  if (targetId == null || targetType !== 'COURSE') return undefined;
  return `${BASE}/lxp/course/${targetId}`;
}

export interface ActivityApplicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** External event id to register against. */
  eventId: number | string;
  /**
   * The event's dynamic application form id (9464). When set, a valid core
   * form advances to a questions step rendered from the form-designer
   * document; the answers ride the register payload as `formValues`.
   */
  applicationFormId?: number | string;
  /** Optional dialog subtitle (e.g. event title). */
  eventTitle?: string;
  /** Optional secondary subtitle line (e.g. city). */
  eventSubtitle?: string;
  /** Fired after a successful registration (e.g. to refetch the feed). */
  onSuccess?: () => void;
  /** Override any subset of the default (Turkish) copy. */
  labels?: Partial<ActivityApplicationLabels>;
}

// --- prefill / payload shapes ----------------------------------------------

interface NamedRef {
  id?: number | string;
  name?: string;
}

interface PrefillResponse {
  personDetails?: {
    name?: string;
    surname?: string;
    identificationNumber?: string;
    email?: string;
    mobile?: string;
    dob?: number;
    currentCity?: NamedRef | null;
  } | null;
  educationDetails?: {
    year?: string;
    educationStatus?: string;
    educationLevel?: string;
    city?: NamedRef | null;
    establishment?: NamedRef | null;
    faculty?: NamedRef | null;
    department?: NamedRef | null;
  } | null;
  /** What the authorities hold; absent when none does (plan D49). */
  verifiedEducationLevel?: string;
  /** Levels some authority speaks for. Everything else is free to declare. */
  restrictedEducationLevels?: string[];
}

interface FormState {
  name: string;
  surname: string;
  identificationNumber: string;
  email: string;
  mobile: string;
  dob: string; // yyyy-mm-dd
  currentCity: AsyncSelectOption | null;
  educationStatus: string;
  educationLevel: string;
  city: AsyncSelectOption | null;
  establishment: AsyncSelectOption | null;
  faculty: AsyncSelectOption | null;
  department: AsyncSelectOption | null;
  year: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  surname: '',
  identificationNumber: '',
  email: '',
  mobile: '',
  dob: '',
  currentCity: null,
  educationStatus: '',
  educationLevel: '',
  city: null,
  establishment: null,
  faculty: null,
  department: null,
  year: '',
};

type Errors = Partial<Record<keyof FormState, string>>;

// --- helpers ---------------------------------------------------------------

function toOption(ref: NamedRef | null | undefined): AsyncSelectOption | null {
  if (!ref || ref.id == null) return null;
  const label = ref.name?.trim();
  // A ref with an id but no name can't be shown to the user (AsyncSelect
  // would render its clear button over a blank, unlabeled field) and can't
  // be told apart from "no selection" — treat it as unselected rather than
  // as a phantom selected-but-invisible option.
  if (!label) return null;
  return { value: String(ref.id), label };
}

function msToInputDate(ms?: number): string {
  if (!ms) return '';
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function normaliseMobile(raw?: string): string {
  if (!raw) return '';
  if (raw.startsWith('+90')) return raw.slice(3);
  if (raw.startsWith('90')) return raw.slice(2);
  return raw;
}

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** Build a remote-search loader for the edu cascade. */
function eduSearcher(resource: string, filter?: Record<string, unknown>) {
  return async (term: string, signal: AbortSignal): Promise<AsyncSelectOption[]> => {
    const res = await fetch(`${BASE}/api/edu/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ resource, searchTerm: term, filter: filter ?? {} }),
      signal,
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { options?: AsyncSelectOption[] };
    return Array.isArray(data.options) ? data.options : [];
  };
}

// ---------------------------------------------------------------------------

export function ActivityApplicationModal({
  open,
  onOpenChange,
  eventId,
  applicationFormId,
  eventTitle,
  eventSubtitle,
  onSuccess,
  labels: labelOverrides,
}: ActivityApplicationModalProps) {
  const { t } = useTranslation('site-builder-blocks');
  const labels = useMemo<ActivityApplicationLabels>(
    () => ({
      title: t('activity.apply.title'),
      note: t('activity.apply.note'),
      accountInformation: t('activity.apply.accountInformation'),
      educationInformation: t('activity.apply.educationInformation'),
      name: t('activity.apply.name'),
      surname: t('activity.apply.surname'),
      identificationNumber: t('activity.apply.identificationNumber'),
      email: t('activity.apply.email'),
      mobile: t('activity.apply.mobile'),
      dob: t('activity.apply.dob'),
      currentCity: t('activity.apply.currentCity'),
      educationStatus: t('activity.apply.educationStatus'),
      educationLevel: t('activity.apply.educationLevel'),
      universityCity: t('activity.apply.universityCity'),
      universityName: t('activity.apply.universityName'),
      facultyName: t('activity.apply.facultyName'),
      departmentName: t('activity.apply.departmentName'),
      className: t('activity.apply.className'),
      notGraduate: t('activity.apply.notGraduate'),
      graduate: t('activity.apply.graduate'),
      preparatoryClass: t('activity.apply.preparatoryClass'),
      cancel: t('activity.apply.cancel'),
      apply: t('activity.apply.apply'),
      success: t('activity.apply.success'),
      emailCheckTitle: t('activity.apply.emailCheckTitle'),
      emailCheckNote: t('activity.apply.emailCheckNote'),
      emailCheckBack: t('activity.apply.emailCheckBack'),
      emailCheckConfirm: t('activity.apply.emailCheckConfirm'),
      errorGeneric: t('activity.apply.errorGeneric'),
      errorBlacklisted: t('activity.apply.errorBlacklisted'),
      errorAgeBelowMin: t('activity.apply.errorAgeBelowMin'),
      errorAgeAboveMax: t('activity.apply.errorAgeAboveMax'),
      errorDateOfBirthRequired: t('activity.apply.errorDateOfBirthRequired'),
      errorEducationLevelRequired: t('activity.apply.errorEducationLevelRequired'),
      errorEducationLevelNotAllowed: t('activity.apply.errorEducationLevelNotAllowed'),
      errorEducationLevelNotVerified: t('activity.apply.errorEducationLevelNotVerified'),
      errorAlreadyAttendedInGroup: t('activity.apply.errorAlreadyAttendedInGroup'),
      errorPrerequisitesNotMet: t('activity.apply.errorPrerequisitesNotMet'),
      errorAlreadyRegistered: t('activity.apply.errorAlreadyRegistered'),
      errorEventNotAvailable: t('activity.apply.errorEventNotAvailable'),
      errorEntriesClosed: t('activity.apply.errorEntriesClosed'),
      errorQuotaFull: t('activity.apply.errorQuotaFull'),
      blockedTitle: t('activity.apply.blockedTitle'),
      blockedPrerequisites: t('activity.apply.blockedPrerequisites'),
      requiredField: t('activity.apply.requiredField'),
      emailField: t('activity.apply.emailField'),
      idNumberField: t('activity.apply.idNumberField'),
      telField: t('activity.apply.telField'),
      noResult: t('activity.apply.noResult'),
      loading: t('activity.apply.loading'),
      select: t('activity.apply.select'),
      ...labelOverrides,
    }),
    [t, labelOverrides],
  );
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Dynamic application form (9464) ──────────────────────────────────────
  const [appFormDef, setAppFormDef] = useState<FormDefinition | null>(null);
  const [appFormValues, setAppFormValues] = useState<Record<string, unknown> | null>(null);
  const [step, setStep] = useState<'core' | 'questions'>('core');

  useEffect(() => {
    setStep('core');
    setAppFormValues(null);
    if (!open || applicationFormId == null) {
      setAppFormDef(null);
      return;
    }
    let cancelled = false;
    fetch(`${BASE}/api/forms/${encodeURIComponent(String(applicationFormId))}`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { form?: unknown } | null) => {
        if (cancelled) return;
        const doc = data?.form;
        // Same shape guard as DynamicFormBlock: a designer document has sections.
        const ok =
          doc != null &&
          typeof doc === 'object' &&
          Array.isArray((doc as { sections?: unknown }).sections);
        setAppFormDef(ok ? (doc as FormDefinition) : null);
      })
      .catch(() => {
        if (!cancelled) setAppFormDef(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, applicationFormId]);

  const appFormValidation: ValidationLabels = useMemo(
    () => ({
      required: t('activity.apply.form.required'),
      tooShort: (n) => t('activity.apply.form.tooShort', { count: n }),
      tooLong: (n) => t('activity.apply.form.tooLong', { count: n }),
      tooSmall: (n) => t('activity.apply.form.tooSmall', { count: n }),
      tooLarge: (n) => t('activity.apply.form.tooLarge', { count: n }),
      pattern: t('activity.apply.form.pattern'),
    }),
    [t],
  );
  /** Email-check step between a valid form and the actual registration (9470). */
  const [confirmOpen, setConfirmOpen] = useState(false);
  /**
   * 9476/9477 pre-flight. Answered when the modal opens, so an applicant who
   * cannot apply is told why BEFORE filling anything in, rather than after
   * submitting. Includes the ön koşul check, which the search-time screen
   * deliberately cannot do.
   */
  const [blocked, setBlocked] = useState<BlockState | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Errors>({});
  // Fields the backend already knows (name/surname) are shown read-only.
  // Null until the prefill answers. Null means "nothing known to be
  // restricted", which leaves every option selectable — the server remains the
  // gate, so an unknown gate must never lock the form.
  const [educationGate, setEducationGate] = useState<EducationLevelGate | null>(null);
  const [locked, setLocked] = useState<{ name: boolean; surname: boolean }>({
    name: false,
    surname: false,
  });

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  // ── Load prefill each time the modal opens ───────────────────────────────
  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    setLoading(true);
    setSubmitError(null);
    setErrors({});
    setConfirmOpen(false);

    // Pre-flight runs alongside the prefill, not before it: the two are
    // independent, and serialising them would double the time to first paint.
    setBlocked(null);
    fetch(`${BASE}/api/activities/${encodeURIComponent(String(eventId))}/eligibility`, {
      headers: { Accept: 'application/json' },
      signal: ctrl.signal,
    })
      .then(async (res) => (res.ok ? ((await res.json()) as EligibilityResponse) : null))
      .then((data) => {
        if (ctrl.signal.aborted || !data || data.eligible !== false) return;
        const reasons = (data.issues ?? [])
          .map((issue) => {
            const key = issue.code ? BLOCK_REASON_LABELS[issue.code] : undefined;
            // Prefer the backend's own sentence — it carries the specifics
            // (the actual minimum age, the event that closed the group).
            return issue.reason || (key ? labels[key] : undefined);
          })
          .filter((reason): reason is string => Boolean(reason));
        const unmet = (data.unmetPrerequisites ?? [])
          .map((p) => ({
            label: p.targetTitle || p.reason || '',
            href: prerequisiteHref(p.targetType, p.targetId),
          }))
          .filter((row) => Boolean(row.label));
        if (reasons.length === 0 && unmet.length === 0) return;
        setBlocked({ reasons, unmet });
      })
      .catch(() => {
        // A pre-flight that cannot answer is not a rejection: leave the form
        // open and let the register POST be the gate it already is.
      });

    fetch(`${BASE}/api/activities/application-form`, {
      headers: { Accept: 'application/json' },
      signal: ctrl.signal,
    })
      .then(async (res) => (res.ok ? ((await res.json()) as PrefillResponse) : null))
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const p = data?.personDetails ?? {};
        const e = data?.educationDetails ?? {};
        setForm({
          name: p.name ?? '',
          surname: p.surname ?? '',
          identificationNumber: p.identificationNumber ?? '',
          email: p.email ?? '',
          mobile: normaliseMobile(p.mobile),
          dob: msToInputDate(p.dob),
          currentCity: toOption(p.currentCity),
          educationStatus: e.educationStatus ?? '',
          educationLevel: e.educationLevel ?? '',
          city: toOption(e.city),
          establishment: toOption(e.establishment),
          faculty: toOption(e.faculty),
          department: toOption(e.department),
          year: e.year ?? '',
        });
        setLocked({ name: Boolean(p.name), surname: Boolean(p.surname) });
        setEducationGate({
          restrictedLevels: data?.restrictedEducationLevels,
          verifiedLevel: data?.verifiedEducationLevel,
        });
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Prefill is best-effort — fall back to an empty (editable) form.
        setForm(EMPTY_FORM);
        setLocked({ name: false, surname: false });
        // No gate rather than a stale one: failing to learn what is restricted
        // must leave the form usable, and the server is the real check.
        setEducationGate(null);
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [open, eventId, labels]);

  // ── Static select options ────────────────────────────────────────────────
  const educationStatusOptions = useMemo(
    () => [
      { value: 'CONTINUING', label: labels.notGraduate },
      { value: 'COMPLETED', label: labels.graduate },
    ],
    [labels.notGraduate, labels.graduate],
  );

  const educationLevelOptions = useMemo(
    () =>
      applyEducationLevelGate(
        Object.entries(EDU_LEVEL_LABEL_KEYS).map(([value, key]) => ({ value, label: t(key) })),
        educationGate,
      ),
    [educationGate, t],
  );

  const classOptions = useMemo(
    () => [
      { value: '0', label: labels.preparatoryClass },
      ...['1', '2', '3', '4', '5', '6'].map((n) => ({ value: n, label: n })),
    ],
    [labels.preparatoryClass],
  );

  // ── Cascade visibility (mirrors v2's ProfileEducationSettingFormFragment) ─
  const showCascade = Boolean(form.educationStatus) && !NON_TERTIARY.includes(form.educationLevel);
  const showEstablishment = showCascade && Boolean(form.city);
  const showFaculty = showEstablishment && Boolean(form.establishment);
  const showDepartment = showFaculty && Boolean(form.faculty);
  const showYear =
    showDepartment && Boolean(form.department) && form.educationStatus !== 'COMPLETED';

  // ── Cascade searchers (re-created when the parent id changes) ─────────────
  const searchCurrentCity = useMemo(() => eduSearcher('city'), []);
  const searchUniversityCity = useMemo(() => eduSearcher('city'), []);
  const searchUniversity = useMemo(
    () => eduSearcher('university', { cityId: form.city?.value }),
    [form.city?.value],
  );
  const searchFaculty = useMemo(
    () => eduSearcher('faculty', { universityId: form.establishment?.value }),
    [form.establishment?.value],
  );
  const searchDepartment = useMemo(
    () => eduSearcher('department', { facultyId: form.faculty?.value }),
    [form.faculty?.value],
  );

  // ── Validation ───────────────────────────────────────────────────────────
  function validate(): Errors {
    const next: Errors = {};
    const req = (k: keyof FormState, present: boolean) => {
      if (!present) next[k] = labels.requiredField;
    };

    req('name', Boolean(form.name.trim()));
    req('surname', Boolean(form.surname.trim()));
    if (!form.identificationNumber.trim()) next.identificationNumber = labels.requiredField;
    else if (!/^[1-9]\d{10}$/.test(form.identificationNumber.trim()))
      next.identificationNumber = labels.idNumberField;
    if (!form.email.trim()) next.email = labels.requiredField;
    else if (!EMAIL_RE.test(form.email.trim())) next.email = labels.emailField;
    if (!form.mobile.trim()) next.mobile = labels.requiredField;
    else if (!/^\d{10}$/.test(form.mobile.trim())) next.mobile = labels.telField;
    req('dob', Boolean(form.dob));
    req('currentCity', Boolean(form.currentCity));
    req('educationStatus', Boolean(form.educationStatus));
    req('educationLevel', Boolean(form.educationLevel));

    if (showCascade) {
      req('city', Boolean(form.city));
      if (showEstablishment) req('establishment', Boolean(form.establishment));
      if (showFaculty) req('faculty', Boolean(form.faculty));
      if (showDepartment) req('department', Boolean(form.department));
      if (showYear) req('year', Boolean(form.year));
    }
    return next;
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  /**
   * Applying is a two-step flow (9470): a valid form opens the email-check
   * dialog, and only confirming there sends the registration. Notifications
   * about the event are delivered by email alone, so a typo here is silent —
   * the extra step is the last point at which the applicant can catch one.
   */
  function handleSubmit() {
    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSubmitError(null);
    if (appFormDef) {
      setStep('questions');
      return;
    }
    setConfirmOpen(true);
  }

  async function submitApplication() {
    // The submit button is about to become `disabled` below. A focused
    // element that gets disabled is force-blurred by the browser, which
    // falls back to focusing `<body>` — and focusing `<body>` scrolls the
    // page to (0,0). Moving focus to the (static, never-disabled) form
    // first pre-empts that jump.
    formRef.current?.focus({ preventScroll: true });
    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      personDetails: {
        name: form.name,
        surname: form.surname,
        identificationNumber: form.identificationNumber,
        email: form.email,
        mobile: `${DEFAULT_COUNTRY_CODE}${form.mobile}`,
        dob: form.dob ? new Date(form.dob).getTime() : null,
        ...(form.currentCity ? { currentCity: { id: form.currentCity.value } } : {}),
      },
      educationDetails: {
        year: form.year || undefined,
        educationStatus: form.educationStatus,
        educationLevel: form.educationLevel,
        ...(form.faculty ? { faculty: { id: form.faculty.value } } : {}),
        ...(form.department ? { department: { id: form.department.value } } : {}),
      },
      ...(appFormValues ? { formValues: appFormValues } : {}),
    };

    try {
      const res = await fetch(
        `${BASE}/api/activities/${encodeURIComponent(String(eventId))}/register`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (res.ok) {
        setConfirmOpen(false);
        onSuccess?.();
        onOpenChange(false);
        return;
      }
      // 9477 — the apply gate rejects with a specific code; show WHY rather
      // than "something went wrong". Falls back to the generic text for
      // genuinely unexpected failures.
      setSubmitError(await blockReason(res, labels));
    } catch {
      setSubmitError(labels.errorGeneric);
    } finally {
      setSubmitting(false);
      // Drop back to the form on failure: that is where the rejection reason
      // and the (possibly at-fault) fields are.
      setConfirmOpen(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[720px]"
        onPointerDownOutside={ignoreSbSelectPortalOutsideClick}
      >
        <DialogHeader>
          <span style={eyebrowStyle}>{labels.title}</span>
          <DialogTitle>{eventTitle || labels.title}</DialogTitle>
          {eventSubtitle ? <DialogDescription>{eventSubtitle}</DialogDescription> : null}
        </DialogHeader>

        {loading ? (
          <div style={skeletonWrapStyle}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} style={skeletonRowStyle} />
            ))}
          </div>
        ) : blocked ? (
          // 9477 — the form is never shown to someone the apply gate would
          // reject. Reasons come from the same service the register POST uses,
          // so this cannot disagree with what a submit would have said.
          <div style={blockedWrapStyle}>
            <p style={blockedTitleStyle}>{labels.blockedTitle}</p>
            <ul style={blockedListStyle}>
              {blocked.reasons.map((reason, i) => (
                <li key={`r${i}`}>{reason}</li>
              ))}
            </ul>
            {blocked.unmet.length > 0 ? (
              <>
                <p style={blockedSubTitleStyle}>{labels.blockedPrerequisites}</p>
                <ul style={blockedListStyle}>
                  {blocked.unmet.map((row, i) => (
                    <li key={`p${i}`}>
                      {row.href ? (
                        <a href={row.href} style={blockedLinkStyle}>
                          {row.label}
                        </a>
                      ) : (
                        row.label
                      )}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <DialogFooter>
              <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
                {labels.cancel}
              </Button>
            </DialogFooter>
          </div>
        ) : step === 'questions' && appFormDef ? (
          <div>
            <p style={noteStyle}>{appFormDef.description || appFormDef.title}</p>
            <DynamicFormRenderer
              definition={appFormDef}
              initialValues={appFormValues ?? undefined}
              submitting={submitting}
              submitLabel={labels.apply}
              labels={appFormValidation}
              onSubmit={(values) => {
                setAppFormValues(values);
                setConfirmOpen(true);
              }}
            />
            {submitError ? <p style={submitErrorStyle}>{submitError}</p> : null}
            <Button
              variant="outline"
              size="lg"
              disabled={submitting}
              onClick={() => setStep('core')}
              style={{ marginTop: 12 }}
            >
              {t('activity.apply.back')}
            </Button>
          </div>
        ) : (
          <form
            ref={formRef}
            tabIndex={-1}
            style={{ outline: 'none' }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <p style={noteStyle}>{labels.note}</p>

            <Divider>{labels.accountInformation}</Divider>

            <div style={gridTwoStyle}>
              <Field error={errors.name}>
                <Input
                  placeholder={labels.name}
                  value={form.name}
                  disabled={locked.name}
                  onChange={(e) => setField('name', e.target.value)}
                  style={fieldControlStyle(Boolean(errors.name))}
                />
              </Field>
              <Field error={errors.surname}>
                <Input
                  placeholder={labels.surname}
                  value={form.surname}
                  disabled={locked.surname}
                  onChange={(e) => setField('surname', e.target.value)}
                  style={fieldControlStyle(Boolean(errors.surname))}
                />
              </Field>
              <Field error={errors.identificationNumber}>
                <Input
                  placeholder={labels.identificationNumber}
                  value={form.identificationNumber}
                  inputMode="numeric"
                  maxLength={11}
                  onChange={(e) =>
                    setField('identificationNumber', e.target.value.replace(/\D/g, ''))
                  }
                  style={fieldControlStyle(Boolean(errors.identificationNumber))}
                />
              </Field>
              <Field error={errors.email}>
                <Input
                  placeholder={labels.email}
                  type="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  style={fieldControlStyle(Boolean(errors.email))}
                />
              </Field>
              <Field error={errors.mobile}>
                <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                  <span style={dialCodeStyle}>+{DEFAULT_COUNTRY_CODE}</span>
                  <Input
                    placeholder={labels.mobile}
                    inputMode="numeric"
                    maxLength={10}
                    value={form.mobile}
                    onChange={(e) => setField('mobile', e.target.value.replace(/\D/g, ''))}
                    style={fieldControlStyle(Boolean(errors.mobile))}
                  />
                </div>
              </Field>
              <Field error={errors.dob}>
                <Input
                  placeholder={labels.dob}
                  type="date"
                  value={form.dob}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setField('dob', e.target.value)}
                  style={fieldControlStyle(Boolean(errors.dob))}
                />
              </Field>
              <Field error={errors.currentCity}>
                <AsyncSelect
                  value={form.currentCity}
                  onChange={(v) => setField('currentCity', v)}
                  fetchOptions={searchCurrentCity}
                  placeholder={labels.currentCity}
                  ariaLabel={labels.currentCity}
                  invalid={Boolean(errors.currentCity)}
                  noResultText={labels.noResult}
                  loadingText={labels.loading}
                />
              </Field>
            </div>

            <Divider>{labels.educationInformation}</Divider>

            <div style={gridTwoStyle}>
              <Field error={errors.educationStatus}>
                <SelectField
                  value={form.educationStatus || undefined}
                  onChange={(v) => setField('educationStatus', v)}
                  options={educationStatusOptions}
                  placeholder={labels.educationStatus}
                  ariaLabel={labels.educationStatus}
                  fullWidth
                  style={selectControlStyle(Boolean(errors.educationStatus))}
                />
              </Field>
              <Field error={errors.educationLevel}>
                <SelectField
                  value={form.educationLevel || undefined}
                  onChange={(v) => setField('educationLevel', v)}
                  options={educationLevelOptions}
                  placeholder={labels.educationLevel}
                  ariaLabel={labels.educationLevel}
                  fullWidth
                  style={selectControlStyle(Boolean(errors.educationLevel))}
                />
              </Field>
            </div>

            {showCascade ? (
              <div style={{ ...gridTwoStyle, marginTop: 12 }}>
                <Field error={errors.city}>
                  <AsyncSelect
                    value={form.city}
                    onChange={(v) => {
                      setField('city', v);
                      setField('establishment', null);
                      setField('faculty', null);
                      setField('department', null);
                    }}
                    fetchOptions={searchUniversityCity}
                    placeholder={labels.universityCity}
                    ariaLabel={labels.universityCity}
                    invalid={Boolean(errors.city)}
                    noResultText={labels.noResult}
                    loadingText={labels.loading}
                  />
                </Field>

                {showEstablishment ? (
                  <Field error={errors.establishment}>
                    <AsyncSelect
                      value={form.establishment}
                      onChange={(v) => {
                        setField('establishment', v);
                        setField('faculty', null);
                        setField('department', null);
                      }}
                      fetchOptions={searchUniversity}
                      reloadKey={String(form.city?.value ?? '')}
                      placeholder={labels.universityName}
                      ariaLabel={labels.universityName}
                      invalid={Boolean(errors.establishment)}
                      noResultText={labels.noResult}
                      loadingText={labels.loading}
                    />
                  </Field>
                ) : null}

                {showFaculty ? (
                  <Field error={errors.faculty}>
                    <AsyncSelect
                      value={form.faculty}
                      onChange={(v) => {
                        setField('faculty', v);
                        setField('department', null);
                      }}
                      fetchOptions={searchFaculty}
                      reloadKey={String(form.establishment?.value ?? '')}
                      placeholder={labels.facultyName}
                      ariaLabel={labels.facultyName}
                      invalid={Boolean(errors.faculty)}
                      noResultText={labels.noResult}
                      loadingText={labels.loading}
                    />
                  </Field>
                ) : null}

                {showDepartment ? (
                  <Field error={errors.department}>
                    <AsyncSelect
                      value={form.department}
                      onChange={(v) => setField('department', v)}
                      fetchOptions={searchDepartment}
                      reloadKey={String(form.faculty?.value ?? '')}
                      placeholder={labels.departmentName}
                      ariaLabel={labels.departmentName}
                      invalid={Boolean(errors.department)}
                      noResultText={labels.noResult}
                      loadingText={labels.loading}
                    />
                  </Field>
                ) : null}

                {showYear ? (
                  <Field error={errors.year}>
                    <SelectField
                      value={form.year || undefined}
                      onChange={(v) => setField('year', v)}
                      options={classOptions}
                      placeholder={labels.className}
                      ariaLabel={labels.className}
                      fullWidth
                      style={selectControlStyle(Boolean(errors.year))}
                    />
                  </Field>
                ) : null}
              </div>
            ) : null}

            {submitError ? <p style={submitErrorStyle}>{submitError}</p> : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="lg"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
              >
                {labels.cancel}
              </Button>
              <Button type="submit" variant="brand" size="lg" disabled={submitting}>
                {submitting ? '…' : labels.apply}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>

      <EmailCheckDialog
        open={confirmOpen}
        labels={labels}
        email={form.email}
        submitting={submitting}
        onEmailChange={(value) => setField('email', value)}
        onBack={() => setConfirmOpen(false)}
        onConfirm={() => void submitApplication()}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Email-check step (9470)
// ---------------------------------------------------------------------------

/**
 * Layered over the application dialog rather than replacing its body: the form
 * stays visible behind the overlay, so confirming reads as a last check on a
 * filled-in application instead of a new screen.
 */
function EmailCheckDialog({
  open,
  labels,
  email,
  submitting,
  onEmailChange,
  onBack,
  onConfirm,
}: {
  open: boolean;
  labels: ActivityApplicationLabels;
  email: string;
  submitting: boolean;
  onEmailChange: (value: string) => void;
  onBack: () => void;
  onConfirm: () => void;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);

  function confirm() {
    const value = email.trim();
    if (!value) {
      setError(labels.requiredField);
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError(labels.emailField);
      return;
    }
    setError(null);
    onConfirm();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onBack();
      }}
    >
      <DialogContent onPointerDownOutside={ignoreSbSelectPortalOutsideClick}>
        <DialogHeader>
          <DialogTitle>{labels.emailCheckTitle}</DialogTitle>
          <DialogDescription>{labels.emailCheckNote}</DialogDescription>
        </DialogHeader>

        <Field error={error ?? undefined}>
          <Input
            placeholder={labels.email}
            type="email"
            value={email}
            autoComplete="email"
            onChange={(e) => {
              onEmailChange(e.target.value);
              setError(null);
            }}
            style={fieldControlStyle(Boolean(error))}
          />
        </Field>

        <DialogFooter>
          <Button type="button" variant="outline" size="lg" disabled={submitting} onClick={onBack}>
            {labels.emailCheckBack}
          </Button>
          <Button type="button" variant="brand" size="lg" disabled={submitting} onClick={confirm}>
            {submitting ? '…' : labels.emailCheckConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- small presentational helpers ------------------------------------------

function Field({ error, children }: { error?: string; children: ReactNode }): ReactNode {
  return (
    <div style={fieldWrapStyle}>
      {children}
      {/* Always rendered (never conditionally mounted) so a field's height
          never changes between valid/invalid states. A failed submit on an
          empty form flips ~8 fields to invalid in one render; if the error
          line only appeared then, every field above the dialog's scroll
          position would grow at once — the scrollTop number stays the same,
          but the DOM above it just got taller, so the same offset now shows
          earlier content. Chrome's scroll anchoring doesn't compensate for
          this (verified), so it reads as the dialog jumping to the top. */}
      <span style={fieldErrorStyle}>{error || ' '}</span>
    </div>
  );
}

function Divider({ children }: { children: ReactNode }): ReactNode {
  return (
    <div style={dividerStyle}>
      <span style={dividerLabelStyle}>{children}</span>
      <span style={dividerLineStyle} />
    </div>
  );
}

// --- styles ----------------------------------------------------------------

const eyebrowStyle: CSSProperties = {
  display: 'block',
  marginBottom: 4,
  fontSize: 'var(--font-size-xs, 12px)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const noteStyle: CSSProperties = {
  margin: '0 0 4px',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111)',
  lineHeight: 1.5,
};

const gridTwoStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 12,
};

const fieldWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
};

const fieldErrorStyle: CSSProperties = {
  display: 'block',
  // Reserves room for two wrapped lines up front. A lone space character
  // alone doesn't reliably reserve height (whitespace-only inline content
  // can collapse), so this is pinned explicitly — see the comment on
  // `Field` above for why the reserved height has to be there before the
  // text is, not just alongside it.
  minHeight: '2.4em',
  fontSize: 'var(--font-size-xs, 12px)',
  lineHeight: 1.2,
  color: 'var(--color-danger-fg, #c0392b)',
};

function fieldControlStyle(invalid: boolean): CSSProperties {
  return {
    height: 40,
    borderRadius: 'var(--radius-md, 10px)',
    ...(invalid ? { borderColor: 'var(--color-danger-fg, #c0392b)' } : {}),
  };
}

function selectControlStyle(invalid: boolean): CSSProperties {
  return {
    height: 40,
    borderRadius: 'var(--radius-md, 10px)',
    ...(invalid ? { borderColor: 'var(--color-danger-fg, #c0392b)' } : {}),
  };
}

const dialCodeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  height: 40,
  padding: '0 12px',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  borderRadius: 'var(--radius-md, 10px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const dividerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '20px 0 14px',
};

const dividerLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  color: 'var(--color-text-secondary, #6b7280)',
  whiteSpace: 'nowrap',
};

const dividerLineStyle: CSSProperties = {
  flex: 1,
  height: 1,
  background: 'var(--color-border-subtle, #e5e7eb)',
};

const submitErrorStyle: CSSProperties = {
  margin: '16px 0 0',
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-danger-fg, #c0392b)',
};

const skeletonWrapStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: '8px 0',
};

const skeletonRowStyle: CSSProperties = {
  height: 40,
  borderRadius: 'var(--radius-md, 10px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
  opacity: 0.7,
};

const blockedWrapStyle: CSSProperties = {
  padding: '4px 0 0',
};

const blockedTitleStyle: CSSProperties = {
  margin: '0 0 8px',
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--destructive, #b91c1c)',
};

const blockedSubTitleStyle: CSSProperties = {
  margin: '14px 0 6px',
  fontSize: 14,
  fontWeight: 600,
};

const blockedListStyle: CSSProperties = {
  margin: 0,
  paddingInlineStart: 20,
  fontSize: 14,
  lineHeight: 1.7,
};

const blockedLinkStyle: CSSProperties = {
  color: 'var(--primary, #1d4ed8)',
  textDecoration: 'underline',
};
